/**
 * Buzz-style sidebar: workspace switcher, search, the active view's content (activity bar: Chat = nav rows and the
 * channel/DM/agent sections; Email = SidebarMail; Docs = the page tree), huddle and profile.
 */
import { Access, Beta, errorKind, GetApp, Profile, Roles, Shell, Sidebar as CoreSidebar, type OpenTarget, type ShellView, type SidebarRow, type SidebarSectionId } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import {
  Avatar,
  Chip,
  Icon,
  IconButton,
  Logo,
  Menu,
  SidebarAgentRow,
  SidebarChannelRow,
  SidebarDmRow,
  SidebarNavRow,
  SidebarSectionHeader,
  Toast,
  useHuddle,
  usePageTree,
  usePreferences,
  useSession,
  useSettingsContext,
  useSidebar,
  useT,
  useTemplatesViewModel,
  useTr,
  useViewModel,
  useWorkspace,
  useWorkflowsSupported,
  openWorkflows,
  type MenuEntry,
  type MenuItem,
  Text,
} from "@bundle/ui";
import { useEffect, useState, useSyncExternalStore } from "react";
import type { Help } from "./HelpMenu.tsx";
import { usePlatform } from "./PlatformContext.ts";
import { ModClick, SidebarDocs } from "./SidebarDocs.tsx";
import { featureFlags } from "../platform/features.ts";
import { isDesktop } from "../platform/desktop.ts";
import { currentPlatform, openDownload, useGetAppState } from "../platform/getApp.ts";
import { SidebarDnd, SidebarSections } from "./SidebarSections.tsx";
import { SidebarHuddle } from "./SidebarHuddle.tsx";
import { SidebarHuddles } from "./SidebarHuddles.tsx";
import { SidebarFiles } from "./SidebarFiles.tsx";
import { SidebarCalendar } from "./SidebarCalendar.tsx";
import { SidebarContacts } from "./SidebarContacts.tsx";
import { SidebarMail } from "./SidebarMail.tsx";
import { SidebarViewSwitch } from "./ActivityBar.tsx";
import { NotificationsOffer, useFeatureAccess, useLater, useMail, useScheduled } from "@bundle/ui";

const SECTION_ICONS: Record<SidebarSectionId, IconName> = { starred: "star", channels: "message", dms: "users", agents: "agent" };

type Props = {
  activeUrl: string;
  open: (url: string, target?: OpenTarget) => void;
  onSearch: () => void;
  onInvitePeople: () => void;
  onInviteGuest: () => void;
  onCreateAgent: () => void;
  /** Channel management + folders (featureFlags.channels). */
  onCreateChannel?: () => void;
  onBrowseChannels?: () => void;
  canSplit: boolean;
  /** The activity bar's view (what the sidebar shows), the views on offer and how to switch (the phone drawer's switch). */
  view: ShellView;
  views: readonly ShellView[];
  onSelectView: (view: ShellView) => void;
  /** The Help menu's rows: in the profile menu below 820px, where the activity bar (and its Help button) is hidden. */
  help?: Help;
};

const NARROW = "(max-width: 819px)";

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia(NARROW).matches);
  useEffect(() => {
    const mq = window.matchMedia(NARROW);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
}

const check = (on: boolean): { icon?: IconName } => (on ? { icon: "check" } : {});

function rowUrl(slug: string, row: SidebarRow): string {
  if (row.type === "agent") return row.channelId === undefined ? Shell.formatUrl({ type: "dm", ws: slug, userId: row.userId }) : Shell.formatUrl({ type: "channel", ws: slug, channelId: row.channelId });
  return Shell.formatUrl({ type: "channel", ws: slug, channelId: row.channelId });
}

export function Sidebar(p: Props) {
  const t = useT();
  const getAppState = useGetAppState();
  const narrow = useNarrow();
  const tr = useTr();
  const ws = useWorkspace();
  const sidebar = useSidebar();
  const session = useSession();
  const prefs = usePreferences();
  const platform = usePlatform();
  const notifications = useViewModel(platform.notifications);
  // Activity's unread items drive its badge, and Do not disturb the moon on my avatar (both need the settings API).
  const settingsCtx = useSettingsContext();
  const activityVm = settingsCtx?.activity;
  const dnd = settingsCtx?.dnd;
  const activityUnread = useSyncExternalStore(activityVm?.subscribe ?? noSubscribe, () => activityVm?.state.unread);
  const paused = useSyncExternalStore(dnd?.subscribe ?? noSubscribe, () => dnd?.state.paused === true);
  // Later: due reminders badge the Later row.
  const later = useLater();
  const laterOverdue = useSyncExternalStore(later?.subscribe ?? noSubscribe, () => later?.state.overdue ?? 0);
  // Scheduled messages: the row shows while I have some (Slack's "Drafts & sent" → Scheduled).
  const scheduled = useScheduled();
  const scheduledCount = useSyncExternalStore(scheduled?.subscribe ?? noSubscribe, () => scheduled?.state.messages.length ?? 0);
  const analyticsOn = useSyncExternalStore(platform.analytics.subscribe, platform.analytics.getSnapshot, platform.analytics.getSnapshot).collecting;
  // A failed "Join <name>" (offline, or the server refused) says so; the list refreshes itself.
  const [joinFailed, setJoinFailed] = useState<"offline" | "error" | undefined>(undefined);
  const cache = ws.state.cache;
  const me = cache?.members[ws.state.meId];
  const name = cache?.workspace.name ?? session.state.session?.workspace?.name ?? "";
  const now = ws.vm.deps.clock.now();
  const canSponsor = me !== undefined && Access.canSponsor(me, now);
  const active = Shell.parseUrl(p.activeUrl);
  const isActive = (url: string) => Shell.normalizeUrl(url) === Shell.normalizeUrl(p.activeUrl);
  const home = Shell.formatUrl({ type: "home", ws: ws.slug });
  const activity = Shell.formatUrl({ type: "activity", ws: ws.slug });
  const laterUrl = Shell.formatUrl({ type: "later", ws: ws.slug });
  const scheduledUrl = Shell.formatUrl({ type: "scheduled", ws: ws.slug });
  const draftsUrl = Shell.formatUrl({ type: "drafts", ws: ws.slug });
  const agents = Shell.formatUrl({ type: "agents", ws: ws.slug });
  const projects = Shell.formatUrl({ type: "projects", ws: ws.slug });
  // Workflow Builder (a sheet; Slack: Tools → Workflows).
  const workflowsOn = useWorkflowsSupported();
  // Mail, Calendar, Contacts, Huddles, Docs and Files have their own activity bar views; Chat keeps the rows below.
  const mail = useMail();
  const view = (p.view === "email" || p.view === "contacts") && mail === undefined ? "chat" : p.view;

  // Switcher: every workspace (current checked), create another, then actions for this one.
  // Areas an admin switched off (core logic/features): their entry points go.
  const access = useFeatureAccess();
  const huddle = useHuddle();
  const workspaceMenu: MenuEntry[] = [];
  const others = session.state.workspaces ?? [];
  for (const w of others.length > 0 ? others : [{ id: ws.vm.wsId, slug: ws.slug, name }]) {
    const current = w.id === ws.vm.wsId;
    workspaceMenu.push({
      id: `ws-${w.id}`,
      label: w.name,
      ...check(current),
      onSelect: () => {
        if (!current) session.vm.openWorkspace(w);
      },
    });
  }
  // Workspaces that allow my verified email's domain: one click joins (as a member) and opens it.
  for (const j of session.state.joinable ?? []) {
    workspaceMenu.push({
      id: `join-${j.id}`,
      label: t("workspace.switcher.join", { name: j.name }),
      icon: "users",
      onSelect: () => {
        if (session.state.joining !== undefined) return;
        setJoinFailed(undefined);
        session.vm.joinWorkspace(j.id).catch((e: unknown) => setJoinFailed(errorKind(e) === "network" ? "offline" : "error"));
      },
    });
  }
  workspaceMenu.push({ id: "create-workspace", label: t("workspace.switcher.create"), icon: "plus", onSelect: () => session.vm.startCreatingWorkspace() });
  if (canSponsor) {
    workspaceMenu.push({ id: "sep-invite", separator: true });
    workspaceMenu.push({ id: "invite-people", label: t("workspace.switcher.invite"), icon: "users", onSelect: p.onInvitePeople });
    workspaceMenu.push({ id: "invite", label: t("invite.guest.action"), icon: "invite", onSelect: p.onInviteGuest });
  }
  // External huddles (meeting links): only with huddles on (the call view model exists).
  const create: MenuItem[] = [];
  if (canSponsor) create.push({ id: "agent", label: t("agent.create.title"), icon: "agent", onSelect: p.onCreateAgent });
  if (huddle !== null) create.push({ id: "new-meeting", label: t("meeting.new.title"), icon: "huddle", onSelect: () => huddle.vm.openNewMeeting() });
  if (create.length > 0) workspaceMenu.push({ id: "sep-create", separator: true }, ...create);
  if (featureFlags.settings && me !== undefined && Roles.canEditWorkspace(me, now)) {
    workspaceMenu.push({ id: "sep-settings", separator: true });
    workspaceMenu.push({ id: "workspace-settings", label: t("settings.menu.workspace"), icon: "settings", onSelect: () => p.open(Shell.formatUrl({ type: "settings", ws: ws.slug, section: "workspace" })) });
  }
  // Signing out lives in the profile menu only.
  if (!featureFlags.settings) workspaceMenu.push({ id: "signout", label: t("auth.signOut"), icon: "signOut", destructive: true, onSelect: () => void platform.signOut() });

  // Profile: language, theme and this device's notifications live in Settings; without Settings they stay here.
  const profileMenu: MenuEntry[] = [];
  if (featureFlags.settings) {
    profileMenu.push({ id: "profile-settings", label: t("settings.menu.profile"), icon: "user", onSelect: () => p.open(Shell.formatUrl({ type: "settings", ws: ws.slug, section: "profile" })) });
    if (dnd !== undefined) {
      profileMenu.push(
        paused
          ? { id: "dnd-resume", label: t("settings.notifications.dnd.resume"), icon: "bell", onSelect: () => void dnd.resume() }
          : { id: "dnd-pause", label: t("settings.notifications.dnd"), icon: "moon", onSelect: () => p.open(Shell.formatUrl({ type: "settings", ws: ws.slug, section: "notifications" })) },
      );
    }
  } else {
    profileMenu.push(
      { id: "lang-en", label: t("language.en"), ...check(prefs.locale === "en"), onSelect: () => prefs.setLocale("en") },
      { id: "lang-pt", label: t("language.ptBR"), ...check(prefs.locale === "pt-BR"), onSelect: () => prefs.setLocale("pt-BR") },
      { id: "sep-theme", separator: true },
      { id: "theme-system", label: `${t("profile.theme")}: ${t("profile.theme.system")}`, ...check(prefs.theme === "system"), onSelect: () => prefs.setTheme("system") },
      { id: "theme-light", label: `${t("profile.theme")}: ${t("profile.theme.light")}`, ...check(prefs.theme === "light"), onSelect: () => prefs.setTheme("light") },
      { id: "theme-dark", label: `${t("profile.theme")}: ${t("profile.theme.dark")}`, ...check(prefs.theme === "dark"), onSelect: () => prefs.setTheme("dark") },
    );
    // Web push for this device (hidden where it can't work: desktop app, unsupported browser, no VAPID key).
    const notify = notifications.status;
    if (notify === "off") {
      profileMenu.push({
        id: "notifications",
        label: notifications.busy ? t("profile.notifications.enabling") : notifications.failed ? t("profile.notifications.retry") : t("profile.notifications.enable"),
        icon: "bell",
        // Straight from the click: the browser only shows its permission prompt for a user gesture.
        onSelect: () => void platform.notifications.enable(),
      });
    } else if (notify === "on") {
      profileMenu.push({ id: "notifications", label: t("profile.notifications.on"), icon: "check", onSelect: () => void platform.notifications.disable() });
    } else if (notify === "blocked") {
      profileMenu.push({ id: "notifications", label: t("profile.notifications.blocked"), icon: "bellOff", onSelect: () => platform.notifications.refresh() });
    }
  }
  // Usage analytics consent: in Settings → Preferences when Settings are on.
  if (platform.analytics.available && !featureFlags.settings) {
    profileMenu.push({ id: "analytics", label: t("profile.analytics"), ...check(analyticsOn), onSelect: () => platform.analytics.setConsent(!analyticsOn) });
  }
  // "Get the app" (design: Get the app prompt): everywhere but inside the desktop app, even after the banner was dismissed.
  if (GetApp.showsMenuEntry(isDesktop())) {
    const platform = currentPlatform();
    profileMenu.push(
      { id: "sep-get-app", separator: true },
      { id: "get-app", label: t(GetApp.menuLabelKey(platform)), icon: "download", onSelect: () => openDownload(GetApp.menuTarget(platform, getAppState.availability)) },
    );
  }
  const showHelp = narrow && p.help !== undefined;
  // The Beta label opens Help: the activity bar's menu, or (below 820px, where Help lives there) the profile menu.
  const [profileOpen, setProfileOpen] = useState(false);
  const openHelp = () => (showHelp ? setProfileOpen(true) : p.help?.setMenuOpen(true));
  if (showHelp) profileMenu.push({ id: "sep-help", separator: true }, ...p.help!.entries);
  profileMenu.push({ id: "sep-signout", separator: true }, { id: "signout", label: t("auth.signOut"), icon: "signOut", destructive: true, onSelect: () => void platform.signOut() });

  return (
    <aside className="sidebar" aria-label={name}>
      <div className="sidebar-top">
        <div className="ws-row">
          <Menu items={workspaceMenu} testID="workspace-menu">
            <button type="button" className="ws-switcher" data-testid="workspace-switcher">
              {cache?.workspace.iconUrl === undefined ? (
                <span className="ws-logo">
                  <Logo variant="symbol" size="icon.sm" color="accent.fg" />
                </span>
              ) : (
                <Avatar name={name} src={cache.workspace.iconUrl} square size="avatar.sm" />
              )}
              <span className="ws-name">{name}</span>
              <Icon name="chevronDown" size="icon.sm" color="text.tertiary" />
            </button>
          </Menu>
          {Beta.BETA && p.help !== undefined ? <Chip label={t("beta.label")} tone="accent" onPress={openHelp} accessibilityLabel={t("beta.label.open")} testID="beta-label" /> : null}
        </div>
        <button type="button" className="search-button" onClick={p.onSearch} data-testid="sidebar-search">
          <Icon name="search" size="icon.sm" />
          <span className="search-label">{t("shell.search.placeholder")}</span>
          <kbd>⌘K</kbd>
        </button>
        <SidebarViewSwitch views={p.views} view={view} onSelect={p.onSelectView} />
      </div>
      {view === "email" && mail !== undefined ? (
        <nav className="sidebar-scroll" data-testid="sidebar-view-email">
          <SidebarMail mail={mail} activeUrl={p.activeUrl} open={p.open} />
        </nav>
      ) : view === "calendar" ? (
        <nav className="sidebar-scroll" data-testid="sidebar-view-calendar">
          <SidebarCalendar activeUrl={p.activeUrl} open={p.open} />
        </nav>
      ) : view === "contacts" ? (
        <nav className="sidebar-scroll" data-testid="sidebar-view-contacts">
          <SidebarContacts activeUrl={p.activeUrl} open={p.open} />
        </nav>
      ) : view === "huddles" && huddle?.area !== undefined ? (
        <nav className="sidebar-scroll" data-testid="sidebar-view-huddles">
          <SidebarHuddles activeUrl={p.activeUrl} open={p.open} />
        </nav>
      ) : view === "files" ? (
        <nav className="sidebar-scroll" data-testid="sidebar-view-files">
          <SidebarFiles activeUrl={p.activeUrl} open={p.open} />
        </nav>
      ) : view === "docs" ? (
        <nav className="sidebar-scroll" data-testid="sidebar-view-docs">
          <DocsViewHeader open={p.open} />
          <SidebarDocs activeUrl={p.activeUrl} open={p.open} canSplit={p.canSplit} />
        </nav>
      ) : (
      <nav className="sidebar-scroll" data-testid="sidebar-view-chat">
        <div className="sidebar-group">
          <SidebarNavRow icon="home" label={t("nav.home")} selected={active?.type === "home"} onPress={() => p.open(home)} testID="nav-home" />
          <SidebarNavRow icon="inbox" label={t("nav.activity")} badge={activityUnread ?? sidebar.state.badge} selected={active?.type === "activity"} onPress={() => p.open(activity)} testID="nav-activity" />
          <SidebarNavRow icon="bookmark" label={t("nav.later")} badge={laterOverdue || undefined} selected={active?.type === "later"} onPress={() => p.open(laterUrl)} testID="nav-later" />
          <SidebarNavRow icon="edit" label={t("nav.drafts")} selected={active?.type === "drafts"} onPress={() => p.open(draftsUrl)} testID="nav-drafts" />
          {scheduledCount > 0 || active?.type === "scheduled" ? (
            <SidebarNavRow icon="send" label={t("nav.scheduled")} selected={active?.type === "scheduled"} onPress={() => p.open(scheduledUrl)} testID="nav-scheduled" />
          ) : null}
          {access("docs") === "none" ? null : (
            <SidebarNavRow icon="board" label={t("nav.projects")} selected={active?.type === "projects"} onPress={() => p.open(projects)} testID="nav-projects" />
          )}
          <SidebarNavRow icon="agent" label={t("nav.agents")} selected={active?.type === "agents"} onPress={() => p.open(agents)} testID="nav-agents" />
          {workflowsOn ? <SidebarNavRow icon="sparkles" label={t("nav.workflows")} selected={false} onPress={() => openWorkflows()} testID="nav-workflows" /> : null}
        </div>
        {featureFlags.channels ? (
          <SidebarDnd>
            <SidebarSections
              activeUrl={p.activeUrl}
              open={p.open}
              canSplit={p.canSplit}
              canSponsor={canSponsor}
              onCreateAgent={p.onCreateAgent}
              onCreateChannel={() => p.onCreateChannel?.()}
              onBrowseChannels={() => p.onBrowseChannels?.()}
            />
          </SidebarDnd>
        ) : null}
        {featureFlags.channels ? null : sidebar.state.sections.map((section) => {
          const collapsed = sidebar.state.collapsed.includes(section.id);
          return (
            <div className="sidebar-group" key={section.id}>
              <SidebarSectionHeader
                title={tr(section.title)}
                icon={SECTION_ICONS[section.id as SidebarSectionId] ?? "folder"}
                collapsed={collapsed}
                onToggle={() => sidebar.vm.toggleSection(section.id)}
                onAdd={section.id === "agents" && canSponsor ? p.onCreateAgent : undefined}
                addLabel={section.id === "agents" ? t("agent.create.title") : undefined}
                testID={`section-${section.id}`}
              />
              {section.rows
                .filter((r): r is SidebarRow => r.type !== "page")
                // Slack: a collapsed section still lists its unread conversations and the one on screen.
                .filter((r) => !collapsed || CoreSidebar.showsWhenCollapsed(r) || isActive(rowUrl(ws.slug, r)))
                .map((row) => {
                    const url = rowUrl(ws.slug, row);
                    const props = {
                      selected: isActive(url) || (row.type === "agent" && active?.type === "dm" && active.userId === row.userId),
                      onPress: () => p.open(url),
                      onOpenInNewTab: () => p.open(url, "newTab"),
                      onOpenInSplit: p.canSplit ? () => p.open(url, "split") : undefined,
                    };
                    return (
                      <ModClick key={row.id} url={url} open={p.open}>
                        {row.type === "channel" ? <SidebarChannelRow row={row} {...props} /> : row.type === "dm" ? <SidebarDmRow row={row} {...props} /> : <SidebarAgentRow row={row} {...props} />}
                      </ModClick>
                    );
                  })}
            </div>
          );
        })}
      </nav>
      )}
      <NotificationsOffer notifications={platform.notifications} />
      <SidebarHuddle open={p.open} canSplit={p.canSplit} />
      <div className="sidebar-bottom">
        <Menu items={profileMenu} open={profileOpen} onOpenChange={setProfileOpen} testID="profile-menu">
          <button type="button" className="profile-button" data-testid="profile-button">
            <Avatar name={me?.displayName ?? "?"} {...(me === undefined ? {} : Profile.avatarLook(me))} size="avatar.sm" presence={ws.state.realtime === "online" ? "online" : "away"} dnd={paused} dndLabel={t("dnd.paused")} />
            <span className="profile-text">
              <span className="profile-name">{me?.displayName ?? ""}</span>
              <span className="profile-sub">{me?.title ?? session.state.session?.email ?? ""}</span>
            </span>
            <Icon name="settings" size="icon.sm" color="text.tertiary" />
          </button>
        </Menu>
      </div>
      <Toast
        open={joinFailed !== undefined}
        onClose={() => setJoinFailed(undefined)}
        message={joinFailed === "offline" ? t("workspace.join.offline") : t("workspace.join.error")}
        closeLabel={t("common.close")}
        durationMs={6000}
        testID="workspace-join-error"
      />
    </aside>
  );
}

const noSubscribe = () => () => {};

/** The Docs view's title row: all docs (the searchable list), the template gallery and a new page. */
function DocsViewHeader({ open }: { open: (url: string, target?: OpenTarget) => void }) {
  const t = useT();
  const ws = useWorkspace();
  const pages = usePageTree();
  const gallery = useTemplatesViewModel();
  const newPage = () =>
    void pages.vm.create({}).then((page) => {
      if (page !== undefined) open(Shell.formatUrl({ type: "page", ws: ws.slug, pageId: page.id }));
    });
  return (
    <div className="sidebar-view-header">
      <Text variant="headline" weight="semibold">
        {t("shell.view.docs")}
      </Text>
      <span className="sidebar-view-actions">
        <IconButton icon="search" size="sm" accessibilityLabel={t("docs.search")} onPress={() => open(Shell.formatUrl({ type: "docs", ws: ws.slug }))} testID="sidebar-docs-search" />
        {gallery === null || !pages.state.canCreatePages ? null : (
          <IconButton icon="sparkles" size="sm" accessibilityLabel={t("templates.open")} onPress={() => gallery.openGallery({})} testID="sidebar-docs-templates" />
        )}
        {pages.state.canCreatePages ? <IconButton icon="pagePlus" size="sm" accessibilityLabel={t("docs.newPage")} onPress={newPage} testID="sidebar-docs-new" /> : null}
      </span>
    </div>
  );
}
